import React, { useState } from "react";
import { Pressable, ScrollView, View } from "react-native";
import CCText from "@/Atoms/CCText";
import CCLoader from "@/Atoms/CCLoader";
import { useRoute, useNavigation } from "@react-navigation/native";
import { useClubCompare } from "@/Hooks/ClubsApi/useClubsQueries";
import { CompareSide } from "@/Hooks/ClubsApi/types";

const AVATAR_COLORS = ["#3DAB9E", "#E17846", "#67B3E1", "#937ADB", "#D1AB41"];
const avatarColor = (name: string) =>
  AVATAR_COLORS[Math.abs(name.charCodeAt(0)) % AVATAR_COLORS.length];

type StatValue = number | null;

const formatValue = (
  left: StatValue,
  right: StatValue,
  type: "rating" | "number" | "percentage",
): { left: string; right: string } => {
  if (type === "rating") {
    return {
      left: left === null ? "Unrated" : String(left),
      right: right === null ? "Unrated" : String(right),
    };
  }
  if (type === "percentage") {
    return {
      left: left === null ? "—" : `${left}%`,
      right: right === null ? "—" : `${right}%`,
    };
  }
  return {
    left: String(left ?? 0),
    right: String(right ?? 0),
  };
};

const highlightStyle = (left: StatValue, right: StatValue) => {
  if (left === null || right === null) return { left: {}, right: {} };
  if (left > right) return { left: { aqua: true }, right: {} };
  if (right > left) return { left: {}, right: { aqua: true } };
  return { left: {}, right: {} };
};

function StatRow({
  label,
  leftVal,
  rightVal,
  type = "number",
}: {
  label: string;
  leftVal: StatValue;
  rightVal: StatValue;
  type?: "rating" | "number" | "percentage";
}) {
  const formatted = formatValue(leftVal, rightVal, type);
  const highlights = highlightStyle(leftVal, rightVal);

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: "rgba(38,35,34,0.10)",
      }}
    >
      {/* Left value */}
      <View style={{ flex: 1, alignItems: "flex-end", paddingRight: 16 }}>
        <CCText
          style={{
            fontSize: 16,
            fontWeight: "700",
            color: highlights.left.aqua ? "#3DAB9E" : "#262322",
          }}
        >
          {formatted.left}
        </CCText>
      </View>

      {/* Label */}
      <View style={{ flex: 1, alignItems: "center", paddingHorizontal: 8 }}>
        <CCText
          style={{
            fontSize: 13,
            fontWeight: "600",
            color: "#888280",
            textAlign: "center",
          }}
        >
          {label}
        </CCText>
      </View>

      {/* Right value */}
      <View style={{ flex: 1, alignItems: "flex-start", paddingLeft: 16 }}>
        <CCText
          style={{
            fontSize: 16,
            fontWeight: "700",
            color: highlights.right.aqua ? "#3DAB9E" : "#262322",
          }}
        >
          {formatted.right}
        </CCText>
      </View>
    </View>
  );
}

function AvatarColumn({ side }: { side: CompareSide }) {
  const avatarSize = 56;
  return (
    <View style={{ alignItems: "center", gap: 8 }}>
      <View
        style={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: avatarSize / 2,
          backgroundColor: avatarColor(side.username),
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CCText style={{ color: "#fff", fontWeight: "700", fontSize: 18 }}>
          {side.username.charAt(0).toUpperCase()}
        </CCText>
      </View>
      <View style={{ alignItems: "center" }}>
        <CCText
          style={{
            fontSize: 14,
            fontWeight: "700",
            color: "#262322",
            textAlign: "center",
          }}
        >
          {side.name || side.username}
        </CCText>
        <CCText
          style={{
            fontSize: 12,
            color: "#888280",
            marginTop: 2,
            textAlign: "center",
          }}
        >
          @{side.username}
        </CCText>
      </View>
    </View>
  );
}

export default function ClubCompare() {
  const route = useRoute() as any;
  const navigation = useNavigation() as any;
  const { clubId, rightId } = route.params;
  const [period, setPeriod] = useState<"daily" | "weekly" | "all">("all");

  const { data, isLoading } = useClubCompare(clubId, undefined, rightId, period);

  if (isLoading || !data) {
    return <CCLoader loading={true} />;
  }

  const { left, right, head_to_head } = data;
  const h2hText = `${head_to_head.left_wins}–${head_to_head.draws}–${head_to_head.right_wins}`;

  const PeriodToggle = () => (
    <View
      style={{
        flexDirection: "row",
        gap: 6,
        paddingHorizontal: 16,
        paddingBottom: 14,
      }}
    >
      {(["daily", "weekly", "all"] as const).map((p) => {
        const active = period === p;
        const labels: Record<typeof p, string> = {
          daily: "Today",
          weekly: "This Week",
          all: "All-time",
        };
        return (
          <Pressable
            key={p}
            onPress={() => setPeriod(p)}
            style={{
              paddingVertical: 7,
              paddingHorizontal: 14,
              borderRadius: 999,
              backgroundColor: active ? "#3DAB9E" : "#fff",
              borderWidth: active ? 0 : 1,
              borderColor: "rgba(38,35,34,0.12)",
            }}
          >
            <CCText
              style={{
                fontWeight: "600",
                color: active ? "#fff" : "#6B584D",
                fontSize: 13,
              }}
            >
              {labels[p]}
            </CCText>
          </Pressable>
        );
      })}
    </View>
  );

  return (
    <ScrollView
      style={{
        flex: 1,
        backgroundColor: "#FAF6EB",
      }}
      contentContainerStyle={{
        paddingVertical: 16,
        paddingHorizontal: 16,
      }}
    >
      {/* Header */}
      <View style={{ marginBottom: 20 }}>
        <CCText
          style={{
            fontSize: 20,
            fontWeight: "700",
            color: "#262322",
            marginBottom: 14,
          }}
        >
          Head-to-Head Comparison
        </CCText>

        {/* Period toggle */}
        <PeriodToggle />
      </View>

      {/* Versus section */}
      <View
        style={{
          flexDirection: "row",
          gap: 20,
          marginBottom: 24,
          paddingHorizontal: 8,
        }}
      >
        <View style={{ flex: 1 }}>
          <AvatarColumn side={left} />
        </View>
        <View
          style={{
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CCText
            style={{
              fontSize: 14,
              fontWeight: "600",
              color: "#888280",
            }}
          >
            VS
          </CCText>
        </View>
        <View style={{ flex: 1 }}>
          <AvatarColumn side={right} />
        </View>
      </View>

      {/* Stats */}
      <View
        style={{
          backgroundColor: "#fff",
          borderRadius: 12,
          padding: 16,
          marginBottom: 20,
        }}
      >
        <StatRow
          label="Rating"
          leftVal={left.rating}
          rightVal={right.rating}
          type="rating"
        />
        <StatRow
          label="Games Played"
          leftVal={left.games_played}
          rightVal={right.games_played}
          type="number"
        />
        <StatRow
          label="Win %"
          leftVal={left.win_pct}
          rightVal={right.win_pct}
          type="percentage"
        />
        <StatRow
          label="Puzzles Solved"
          leftVal={left.puzzles_solved}
          rightVal={right.puzzles_solved}
          type="number"
        />
        <StatRow
          label="1st Attempt Solved"
          leftVal={left.first_attempt_solved}
          rightVal={right.first_attempt_solved}
          type="number"
        />
      </View>

      {/* Head-to-head */}
      <View
        style={{
          backgroundColor: "#f5f5f5",
          borderRadius: 12,
          padding: 16,
          alignItems: "center",
        }}
      >
        <CCText
          style={{
            fontSize: 12,
            fontWeight: "600",
            color: "#888280",
            marginBottom: 8,
          }}
        >
          Head-to-Head Record
        </CCText>
        <CCText
          style={{
            fontSize: 28,
            fontWeight: "700",
            color: "#262322",
            fontVariant: ["tabular-nums"],
          }}
        >
          {h2hText}
        </CCText>
        <CCText
          style={{
            fontSize: 11,
            color: "#A8A7A6",
            marginTop: 4,
            textAlign: "center",
          }}
        >
          {left.name || left.username} Wins – Draws – {right.name || right.username} Wins
        </CCText>
      </View>
    </ScrollView>
  );
}
